<template>
  <div>
    <h2 class="content-block">Row #{{ id }}</h2>
    <div class="dx-fieldset">
      <div class="dx-fieldset-header">Row Information</div>
      <div class="dx-field">
        <div class="dx-field-label">Id</div>
        <div class="dx-field-value">{{ id }}</div>
      </div>
      <div class="dx-field">
        <div class="dx-field-label">Comparison Source</div>
        <div class="dx-field-value">
          {{
			row.gateway && row.source &&
			gatewayLookup[row.gateway].sources[row.source].name
          }}
          ({{ row.gateway + "_" + row.source }})
        </div>
      </div>
      <div class="dx-field">
        <div class="dx-field-label">Id at comparison source</div>
        <div class="dx-field-value">{{ row.idAtComparisonSource }}</div>
      </div>
      <div class="dx-field">
        <div class="dx-field-label">Id at gateway</div>
        <div class="dx-field-value">{{ row.idAtGateway }}</div>
      </div>
      <div class="dx-field">
        <div class="dx-field-label">Created timestamp</div>
        <div class="dx-field-value">
          <double-time
            :timestamp="row.timestamp"
            :comparison-source-id="row.gateway + '_' + row.source"
          />
        </div>
      </div>
      <div class="dx-field">
        <div class="dx-field-label">Amount</div>
        <div class="dx-field-value">{{ row.amount }}</div>
      </div>
      <div class="dx-field">
        <div class="dx-field-label">Currency</div>
        <div class="dx-field-value">
          {{ row.currencyId && currencyLookup[row.currencyId].iso }}
          ({{ row.currencyId }})
        </div>
      </div>
      <div class="dx-field">
        <div class="dx-field-label">Row type</div>
        <div class="dx-field-value">{{ row.rowType }}</div>
      </div>
      <div class="dx-field">
        <div class="dx-field-label">Reversal</div>
        <div class="dx-field-value">
          {{ row.reversal ? 'Yes' : 'No' }}
        </div>
      </div>
      <div class="dx-field">
        <div class="dx-field-label">Counter party</div>
        <div class="dx-field-value">{{ row.counterParty }}</div>
      </div>
      <div v-if="row.deleted !== null" class="dx-field">
        <div class="dx-field-label">Deleted at</div>
        <div class="dx-field-value">
          {{ formatLongDate(row.deleted) }}
        </div>
      </div>
      <div v-if="row.deleted !== null" class="dx-field">
        <div class="dx-field-label">Deleted by</div>
        <div class="dx-field-value">
          <user-details :user-id="row.userIdDeleted" empty-text="Automatic" />
        </div>
      </div>
    </div>

    <h3 class="content-block">Matches</h3>
    <dx-data-grid
      :data-source="dataSourceMatches"
      :show-borders="true"
      :remote-operations="false"
      :allow-column-reordering="false"
      :allow-column-resizing="true"
      :row-alternation-enabled="true"
      :hover-state-enabled="true"
      :word-wrap-enabled="true"
      :column-auto-width="true"
      :show-row-lines="true"
      :show-column-lines="true"
      class="dx-card wide-card"
      width="100%"
      @row-click="showMatch"
    >
      <dx-column-chooser :enabled="false" />
      <dx-export
        :enabled="true"
        :file-name="`${currentDate}-${$route.name}-matches-${id}`"
      />
      <dx-paging :page-size="pageSizes[0]" />
      <dx-pager
        :allowed-page-sizes="pageSizes"
        :show-page-size-selector="true"
        :show-navigation-buttons="true"
        :show-info="true"
      />
      <dx-filter-row :visible="true" />
      <dx-header-filter :visible="false" />
      <dx-filter-panel :visible="true" />
      <dx-column data-field="id" :format="config.idFormat" />
      <dx-column
        data-field="created"
        data-type="datetime"
        :format="config.intlDateTimeFormat"
      />
      <dx-column
        data-field="userIdCreated"
        :lookup="userColumnLookup"
        caption="Created By"
      />
      <dx-column
        data-field="deleted"
        data-type="datetime"
        :format="config.intlDateTimeFormat"
      />
      <dx-column
        data-field="userIdDeleted"
        :lookup="userColumnLookup"
        caption="Deleted By"
      />
    </dx-data-grid>

    <h3 class="content-block">Statements with Row</h3>
    <dx-data-grid
      :data-source="dataSourceStatements"
      :show-borders="true"
      :remote-operations="false"
      :allow-column-reordering="false"
      :allow-column-resizing="true"
      :row-alternation-enabled="true"
      :hover-state-enabled="true"
      :word-wrap-enabled="true"
      :column-auto-width="true"
      :show-row-lines="true"
      :show-column-lines="true"
      class="dx-card wide-card"
      width="100%"
      @row-click="showStatement"
    >
      <dx-column-chooser :enabled="false" />
      <dx-export
        :enabled="true"
        :file-name="`${currentDate}-${$route.name}-statements-${id}`"
      />
      <dx-paging :page-size="pageSizes[0]" />
      <dx-pager
        :allowed-page-sizes="pageSizes"
        :show-page-size-selector="true"
        :show-navigation-buttons="true"
        :show-info="true"
      />
      <dx-filter-row :visible="true" />
      <dx-header-filter :visible="false" />
      <dx-filter-panel :visible="true" />
      <dx-column data-field="id" :format="config.idFormat" />
      <dx-column
        data-field="statementFormatId"
        :lookup="statementFormatColumnLookup"
        caption="Statement format"
      />
      <dx-column
        data-field="saved"
        data-type="datetime"
        :format="config.intlDateTimeFormat"
      />
      <dx-column
        data-field="imported"
        data-type="datetime"
        :format="config.intlDateTimeFormat"
      />
      <dx-column
        data-field="matched"
        data-type="datetime"
        :format="config.intlDateTimeFormat"
      />
      <dx-column
        data-field="deleted"
        data-type="datetime"
        :format="config.intlDateTimeFormat"
      />
    </dx-data-grid>
  </div>
</template>

<script>
	import UserDetails from '@/components/user-details'
	import {mapState} from 'vuex'
	import {
		DxColumn,
		DxColumnChooser,
		DxDataGrid,
		DxExport,
		DxFilterPanel,
		DxFilterRow,
		DxHeaderFilter,
		DxPager,
		DxPaging,
	} from 'devextreme-vue/data-grid'
	import DataSource from 'devextreme/data/data_source'
	import {DxTagBox} from 'devextreme-vue'
	import {http} from '@/utils/http'
	import {config} from '@/config'
	import DoubleTime from '@/components/double-time'

	export default {
  name: 'ViewRow',
  components: {
    DxColumn,
    DxColumnChooser,
    DxDataGrid,
    DxExport,
    DxFilterRow,
    DxFilterPanel,
    DxHeaderFilter,
    DxPager,
    DxPaging,
    // eslint-disable-next-line vue/no-unused-components
    DxTagBox,
    UserDetails,
    DoubleTime,
  },
  data() {
    return {
      id: this.$route.path.split('/').pop(),
      config,
      dataSourceStatements: null,
      dataSourceMatches: null,
      pageSizes: config.itemsPerPage,
      row: {
        gateway: null,
        source: null,
        idAtComparisonSource: null,
        idAtGateway: null,
        timestamp: null,
        amount: null,
        currencyId: null,
        rowType: null,
        reversal: null,
        counterParty: null,
        deleted: null,
        userIdDeleted: null,
      },
    }
  },
  computed: {
    ...mapState('user', [
      'statementFormatColumnLookup',
      'statementSourceLookup',
      'gatewayLookup',
      'currencyLookup',
      'userColumnLookup',
    ]),
    currentDate() {
      return new Date().toISOString().split('T')[0]
    },
  },
  created() {
    this.loadData()
  },
  destroyed() {
    this.dataSourceStatements && this.dataSourceStatements.dispose()
    this.dataSourceMatches && this.dataSourceMatches.dispose()
  },
  methods: {
    async loadData() {
      const response = await http
        .get('reconciliation/admin/row/' + this.id)
        .json()
      if (this.dataSourceStatements) {
        this.dataSourceStatements.dispose()
      }

      this.row = response.row

      const statements = response.statements
      this.dataSourceStatements = new DataSource(statements)

      const matches = response.matches
      this.dataSourceMatches = new DataSource(matches)
    },
    showMatch(event) {
      this.$router.push({ path: `/match/${event.data.id}` })
    },
    showStatement(event) {
      this.$router.push({ path: `/statement/${event.data.id}` })
    },
  },
}
</script>
<style lang="scss">
td[role='columnheader'] {
  text-align: center !important;
}

.dx-button {
  float: left !important;
}
</style>
